import React, { useState, useEffect } from "react";
import api, { setupAxiosInterceptors } from "./axiosSetup";
import { useNavigate } from "react-router-dom";
import "./css/Notification.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import CustomNavbar from "./CustomNavbar";

export default function Notifications({ standalone = false }) {
  const [notifications, setNotifications] = useState([]);
  const [hiddenNotifications, setHiddenNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("shown");
  const [openIndex, setOpenIndex] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setupAxiosInterceptors(navigate);
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      const response = await api.get(`/api/notification`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!Array.isArray(response.data)) return;

      setNotifications(response.data.filter((n) => n.isHidden === 0));
      setHiddenNotifications(response.data.filter((n) => n.isHidden === 1));
      setError(null);
    } catch (err) {
      setError("Fehler beim Laden der Mitteilungen.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("de-DE");

  const toggleExpand = (index) =>
    setOpenIndex(openIndex === index ? null : index);

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem("authToken");
      await api.post(
        "/api/notification-read",
        { notificationId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNotifications((prev) =>
        prev.map((n) =>
          n.ID === notificationId ? { ...n, isRead: true } : n
        )
      );
      setHiddenNotifications((prev) =>
        prev.map((n) =>
          n.ID === notificationId ? { ...n, isRead: true } : n
        )
      );
    } catch (err) {
      console.error("Fehler beim Markieren:", err);
    }
  };

  const hideNotification = async (notificationId) => {
    try {
      const token = localStorage.getItem("authToken");
      await api.post(
        "/api/notification-hide",
        { notificationId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const entry = notifications.find((n) => n.ID === notificationId);
      setNotifications((prev) => prev.filter((n) => n.ID !== notificationId));
      setHiddenNotifications((prev) => [...prev, entry]);
    } catch (err) {
      console.error("Fehler beim Ausblenden:", err);
    }
  };

  const unhideNotification = async (notificationId) => {
    try {
      const token = localStorage.getItem("authToken");
      await api.post(
        "/api/notification-unhide",
        { notificationId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const entry = hiddenNotifications.find((n) => n.ID === notificationId);
      setHiddenNotifications((prev) =>
        prev.filter((n) => n.ID !== notificationId)
      );
      setNotifications((prev) => [...prev, entry]);
    } catch (err) {
      console.error("Fehler beim Wiederherstellen:", err);
    }
  };

  const priorityBadge = (prio) => {
    if (prio === 1)
      return <span className="badge bg-danger">Sehr hoch</span>;
    if (prio === 2)
      return <span className="badge bg-warning text-dark">Medium</span>;
    return <span className="badge bg-info text-dark">Info</span>;
  };

  const EmptyState = ({ text }) => (
    <div className="empty-state-card shadow-sm">
      <div className="empty-icon">📭</div>
      <h5 className="fw-semibold mt-2" style={{ color: "#111" }}>
        {text}
      </h5>
      <button className="btn btn-primary" onClick={fetchNotifications}>
        Neu laden
      </button>
    </div>
  );

  const NotificationCard = ({ notification, index, hidden = false }) => (
    <div
      className={`notification-card shadow-sm ${
        notification.isRead ? "read" : "unread"
      }`}
      style={{ color: "#000" }}
    >
      <div
        className="notification-header"
        onClick={() => toggleExpand(index)}
        style={{ cursor: "pointer" }}
      >
        <div className="d-flex flex-column flex-sm-row justify-content-between w-100">
          <div className="d-flex align-items-center gap-2">
            {priorityBadge(notification.priority)}
            <span className="fw-semibold">{notification.title}</span>
          </div>

          <div className="text-muted small">
            {formatDate(notification.publishDate)}
          </div>
        </div>
      </div>

      {openIndex === index && (
        <div className="notification-body-wrapper">
          <div className="notification-body">
            <p className="m-0">
              <strong>Erstellt von:</strong> {notification.createdBy}
            </p>

            <p className="mt-2 mb-1">
              <strong>Inhalt:</strong>
            </p>

            <div
              className="notification-content-box"
              style={{
                whiteSpace: "pre-wrap",
                color: "#000",
              }}
            >
              {notification.body}
            </div>

            <div className="mt-3 d-flex gap-2 flex-wrap">
              {!hidden && (
                <>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      hideNotification(notification.ID);
                    }}
                  >
                    <i className="bi bi-eye-slash"></i> Ausblenden
                  </button>
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={(e) => {
                      e.stopPropagation();
                      markAsRead(notification.ID);
                    }}
                  >
                    <i className="bi bi-check2-square"></i> Gelesen
                  </button>
                </>
              )}

              {hidden && (
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    unhideNotification(notification.ID);
                  }}
                >
                  <i className="bi bi-arrow-return-left"></i> Wiederherstellen
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
    {standalone && <CustomNavbar />}
    <div className="container my-3">

      {/* Mitteilungen weiß */}
      <h3 className="mb-3" style={{ color: "#fff" }}>Mitteilungen</h3>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="notification-tabs mb-3 d-flex gap-2">
        <button
          className={`btn ${
            activeTab === "shown" ? "btn-primary" : "btn-outline-primary"
          }`}
          onClick={() => setActiveTab("shown")}
        >
          Angezeigt
        </button>

        <button
          className={`btn ${
            activeTab === "hidden" ? "btn-primary" : "btn-outline-primary"
          }`}
          onClick={() => setActiveTab("hidden")}
        >
          Ausgeblendet
        </button>
      </div>

      {loading ? (
        <p style={{ color: "#fff" }}>Mitteilungen werden geladen…</p>
      ) : activeTab === "shown" ? (
        notifications.length === 0 ? (
          <EmptyState text="Keine Mitteilungen vorhanden" />
        ) : (
          notifications.map((n, i) => (
            <NotificationCard
              key={n.ID}
              notification={n}
              index={i}
              hidden={false}
            />
          ))
        )
      ) : hiddenNotifications.length === 0 ? (
        <EmptyState text="Keine ausgeblendeten Mitteilungen" />
      ) : (
        hiddenNotifications.map((n, i) => (
          <NotificationCard
            key={n.ID}
            notification={n}
            index={i}
            hidden={true}
          />
        ))
      )}
    </div>
    </>
  );
}